<template>
  <div class="address-lookup">
    <address-card
      v-if="selected"
      :address="selected"
      class="address-lookup__address-card"
      @thrive-clear="clear"
    />
    <div v-else class="address-lookup__search">
      <v-text-field
        :id="addressLookupSearchInputId"
        ref="input"
        v-model="value"
        :class="{ 'address-lookup__search-input--active': isActive }"
        :has-error="hasErrorState"
        :aria-owns="addressLookupDropdownListId"
        :aria-activedescendant="hovered && hovered.Id"
        :aria-expanded="String(isActive)"
        autocomplete="new-password"
        class="address-lookup__search-input"
        label="Search by postcode or address"
        name="addressLookup"
        append-icon="search"
        role="combobox"
        v-on="$listeners"
        @click.stop
        @focus="onFocus"
        @blur="onBlur"
        @keydown.native.esc.prevent="isActive = false"
        @keydown.native.tab="tabPressed"
        @keydown.native.enter.prevent="enterPressed"
        @keydown.native.up.prevent="keyArrows('up')"
        @keydown.native.down.prevent="keyArrows('down')"
      />
      <transition name="address-lookup__fade">
        <div v-show="isActive" ref="dropdown" class="address-lookup__dropdown">
          <div class="address-lookup__keep-typing">
            Keep typing to see more results
          </div>
          <ul
            :id="addressLookupDropdownListId"
            ref="list"
            class="address-lookup__dropdown-list"
            role="listbox"
            aria-label="Address results"
          >
            <template v-if="addressList.length">
              <li
                v-for="(option, index) in addressList"
                :id="option.Id"
                ref="listItem"
                :key="index"
                :class="{
                  'address-lookup__dropdown-option--hover': isOptionHovered(
                    option
                  )
                }"
                :aria-selected="isOptionHovered(option)"
                class="address-lookup__dropdown-option"
                role="option"
                tabindex="-1"
                @click.stop="onOptionSelect(option)"
                @mouseover="setHovered(option)"
              >
                <span
                  class="address-lookup__dropdown-option-text"
                  v-html="`${getOptionText(option.Text, option.Highlight, 0)} `"
                />
                <span
                  class="address-lookup__dropdown-option-description"
                  v-html="
                    getOptionText(option.Description, option.Highlight, 1)
                  "
                />
              </li>
            </template>
            <template v-else>
              <li
                class="address-lookup__dropdown-option address-lookup__dropdown-option--disabled"
                tabindex="-1"
              >
                <span class="address-lookup__dropdown-option-text">
                  We can't find your address
                </span>
              </li>
            </template>
          </ul>
        </div>
      </transition>
    </div>
    <v-alert v-if="error" dense type="error" class="address-lookup__error">{{
      error
    }}</v-alert>
  </div>
</template>

<script>
import debounce from 'lodash.debounce';

import ADDRESS_PROVIDERS from '../../constants/addressProviders';
import uid from '../../mixins/uid';
import AddressCard from './AddressCard';
import LoqateService from './LoqateService';

export default {
  name: 'AddressLookup',
  components: {
    AddressCard
  },
  mixins: [uid],
  model: {
    prop: 'address',
    event: 'thrive-change'
  },
  props: {
    address: {
      type: Object,
      default: null
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      addressList: [],
      error: null,
      hasFocus: false,
      hovered: null,
      isActive: false,
      selected: null,
      value: null
    };
  },
  computed: {
    addressLookupDropdownListId() {
      return `address-lookup-dropdown-list-${this.uid}`;
    },
    addressLookupSearchInputId() {
      return `address-lookup-search-input-${this.uid}`;
    },
    hasErrorState() {
      return !!(this.hasError || (this.error && this.error.length));
    }
  },
  watch: {
    isActive(active) {
      if (!active) this.$nextTick(() => this.setHovered(null));
    },
    value: debounce(async function(value) {
      if (value && value.trim()) {
        await this.findAddress();
        if (this.hasFocus) this.isActive = !!value;
      }
    }, 250)
  },
  created() {
    if (typeof window !== 'undefined') {
      document.addEventListener('click', this.setInactive);
    }
  },
  mounted() {
    if (this.address && this.address.addressProviderId) {
      this.selected = this.address;
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      document.removeEventListener('click', this.setInactive);
    }
  },
  methods: {
    clear() {
      this.value = null;
      this.selected = null;
      this.addressList = [];
      this.$emit('change-address');
      this.$nextTick(() => this.$refs.input.focus());
    },
    enterPressed() {
      if (this.hovered === null) return;
      this.onOptionSelect(this.hovered);
    },
    async findAddress(container) {
      let params = {
        Text: this.value.trim()
      };
      if (container) params.Container = container;
      try {
        let addressList = await LoqateService.findAddress(params);
        this.error = null;
        this.addressList = addressList;
      } catch (err) {
        this.isActive = false;
        this.selected = null;
        this.error = 'Sorry, this isn’t working at the moment.';
        throw err;
      }
    },
    async retrieveAddress(Id) {
      let params = {
        Id,
        Text: this.value.trim()
      };
      try {
        let address = await LoqateService.retrieveAddress(params);
        let coords = await LoqateService.getCoordinates({
          Location: address.PostalCode
        });

        if (coords.Latitude && coords.Longitude) {
          address.latlng = {
            lat: coords.Latitude,
            lng: coords.Longitude
          };
        }

        this.error = null;
        this.selected = address;
        this.$emit('thrive-change', {
          ...this.selected,
          addressProviderId: ADDRESS_PROVIDERS.UNKNOWN
        });
      } catch (err) {
        this.selected = null;
        this.error = 'Sorry, this isn’t working at the moment.';
        throw err;
      } finally {
        this.isActive = false;
      }
    },
    getOptionText(text, highlight, position) {
      let highlightRules = highlight.split(';')[position];
      return this.getHighlightedMarkup(text, highlightRules);
    },
    getHighlightedMarkup(text, highlightRules) {
      if (highlightRules) {
        let splitPoints = highlightRules.split(/,|-/).map(Number);
        let chunks = [];
        splitPoints.forEach((point, index) => {
          let prev = point === 0 ? 0 : splitPoints[index - 1];
          chunks.push(text.substring(prev, point));
          index % 2 === 0 && chunks.push('<em>');
          index % 2 === 1 && chunks.push('</em>');
          index + 1 === splitPoints.length &&
            chunks.push(text.substring(point, text.length));
        });
        return chunks.join('');
      }
      return text;
    },
    keyArrows(direction) {
      let moveBy = direction === 'down' ? 1 : -1;
      if (this.isActive) {
        let index = this.addressList.indexOf(this.hovered) + moveBy;
        index =
          index > this.addressList.length - 1 ? this.addressList.length : index;
        index = index < 0 ? 0 : index;
        this.setHovered(this.addressList[index]);

        let list = this.$refs.list;
        let element = this.$refs.listItem[index];
        if (!element) return;
        let visMin = list.scrollTop;
        let visMax = list.scrollTop + list.clientHeight - element.clientHeight;

        if (element.offsetTop < visMin) {
          list.scrollTop = element.offsetTop;
        } else if (element.offsetTop >= visMax) {
          list.scrollTop =
            element.offsetTop - list.clientHeight + element.clientHeight;
        }
      } else {
        this.isActive = true;
      }
    },
    onFocus() {
      if (this.value && this.value.length > 0) this.isActive = true;
      this.hasFocus = true;
    },
    onBlur() {
      this.hasFocus = false;
    },
    setHovered(option) {
      if (option === undefined) return;
      this.hovered = option;
    },
    onOptionSelect(option) {
      if (option.Type.toUpperCase() === 'ADDRESS') {
        this.retrieveAddress(option.Id);
      } else {
        this.findAddress(option.Id);
      }
    },
    setInactive() {
      this.isActive = false;
    },
    tabPressed() {
      if (this.hovered === null) {
        this.isActive = false;
        return;
      }
      this.onOptionSelect(this.hovered);
    },
    isOptionHovered(option) {
      return this.hovered && option.Id === this.hovered.Id;
    }
  }
};
</script>

<style lang="scss">
.address-lookup {
  $root: &;

  &__search {
    position: relative;

    &-input {
      position: relative;
      z-index: 1;

      &--active input {
        border-color: #422d68;
      }
    }
  }

  &__error {
    margin-top: 0;
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    z-index: 1;
    margin-top: -21px;
    width: 100%;
    background-color: #fff;
    border-color: #422d68;
    border-style: solid;
    border-width: 0 2px 2px;
    box-shadow: 0 2px 9px 0 rgba(#9b9b9b, 0.5);

    &-list {
      position: relative;
      max-height: 260px;
      margin: 0;
      padding: 0 !important;
      overflow-y: scroll;
      list-style: none;

      &::-webkit-scrollbar {
        -webkit-appearance: none;
      }

      &::-webkit-scrollbar:vertical {
        width: 10px;
      }

      &::-webkit-scrollbar-thumb {
        background-color: rgba(#000, 0.4);
        border: 2px solid white;
        border-radius: 8px;
      }
    }

    &-option {
      margin: 0;
      padding: 16px 20px;
      font-size: 14px;
      outline: none;
      cursor: pointer;

      &--hover {
        background-color: #e7dbff;
      }

      &--disabled {
        cursor: default;

        &#{$root}--hover {
          background-color: transparent;
        }
      }

      em {
        font-weight: 700;
        font-style: normal;
      }
    }
  }

  &__keep-typing {
    position: relative;
    z-index: 1;
    margin-bottom: 4px;
    padding: 9px 20px;
    font-size: 14px;
    background-color: #eee;
  }

  &__fade-enter-active,
  &__fade-leave-active {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  &__fade-enter,
  &__fade-leave-to {
    transform: translateY(-6px);
    opacity: 0;
  }
}
</style>
